import { useEffect, useMemo, useRef, useState } from 'react';
import { PencilSimple } from '@phosphor-icons/react';
import type { ExpectedField, ImportBatchRow, ImportTarget, RowChangeSet } from '@discovr/types';
import { UNDO_TOAST_MS, isEmptyChangeSet, type LastRowChange } from '@discovr/features';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { PhaseSubTabs } from '../parts/PhaseSubTabs';
import { DuplicatesTab } from './DuplicatesTab';
import { ExistingCreatorsBar } from './ExistingCreatorsBar';
import { ImportReviewGrid, type EditTarget } from './ImportReviewGrid';
import { WizardFooter } from './ImportWizardFooterSlot';
import { ReviewIssuesBanner } from './ReviewIssuesBanner';
import { alreadyOnCampaignHandles, RowNumberOffsetContext } from './rowNumberDisplay';
import { countImportable } from './reviewRowStatus';
import { buildReviewTabs, initialReviewTab, tabCount, tabLabel, type ReviewTab } from './reviewTabs';
import {
  countBlocking,
  countLabel,
  draftChangeMessage,
  draftHasChanges,
  draftToChangeSet,
  gridFields,
  hasRosterMatches,
  isImportDisabled,
  isSkipDisabled,
  reviewIssuesMessage,
  rowsInOrder,
  startDraft,
  tabRowOrder,
  tabRows,
  type GridDraft,
  type RowOrderSnapshot,
} from './reviewGridDraft';

/** Marks a part of the step that handles Escape itself (the Back
 *  confirmation), so the wizard's dialog must not close on it. */
export const HANDLES_ESCAPE_SELECTOR = '[data-handles-escape]';

/** Live commit progress while the import runs with the grid still shown. */
export interface ImportingProgress {
  done: number;
  total: number;
}

export interface ImportReviewStepProps {
  /** Default 'pipeline'. An outreach batch has no Sheet/Current choice: every
   *  creator already in the campaign is skipped. */
  target?: ImportTarget;
  rows: ImportBatchRow[];
  expectedFields: ExpectedField[];
  mapping: Record<string, string>;
  campaignPlatform: string;
  /** Lets edit mode's live checks agree with the server on whether a ₹0
   *  price counts as pricing a deliverable. */
  payoutModel?: string;
  busy: boolean;
  error: string | null;
  hasSavedChanges: boolean;
  lastChange: LastRowChange | null;
  driftRowNumbers: number[];
  onSaveRows: (changes: RowChangeSet, message: string) => Promise<void>;
  onUndo: () => Promise<void>;
  onDismissChange: () => void;
  onBack: () => Promise<void>;
  onImport: (opts: { skipBlocking: boolean }) => Promise<void>;
  /** Throws the whole import away. Omit to hide the Discard import button. */
  onDiscard?: () => Promise<void>;
  /** Neutral line above the grid, e.g. that this import was already running. */
  notice?: string | null;
  /** The server read the engagement column as fractions and converted it to
   *  percentages; the review says so. */
  engagementReadAsFraction?: boolean;
  /** Lets the wizard stop its dialog closing on Esc while the grid is being
   *  edited — Radix handles Escape on the document before the grid sees it. */
  onEditingChange?: (editing: boolean) => void;
  /** Set while the commit runs: every control is disabled and the primary
   *  button shows the progress. */
  importing?: ImportingProgress | null;
  /** Pasted handles: Go counts a header row the paste never showed, so the review numbers from 1 by subtracting this. */
  rowNumberOffset?: number;
}

const TAB_ORDER: readonly ReviewTab[] = ['all', 'needs-fixing', 'duplicates', 'ready'];
const EMPTY_TAB_TEXT: Record<ReviewTab, string> = {
  all: 'No rows left in this sheet.',
  'needs-fixing': 'Nothing left to fix.',
  duplicates: 'No duplicates left',
  ready: 'No rows are ready yet.',
};
const ENGAGEMENT_FRACTION_NOTICE = 'Engagement was written as fractions (e.g. 0.0794), so it was read as percentages (7.94%).';
const BACK_CONFIRM_TEXT = 'Re-matching columns discards your changes to this sheet.';
const DISCARD_IMPORT_CONFIRM_TEXT = 'Discard this import? Everything you reviewed in it is lost.';
const NOTHING_TO_IMPORT_TEXT = 'Nothing to import: every row is already in the campaign, skipped or removed';
const DISCARD_EDITS_TEXT = 'Cancelling discards the edits you have not saved.';
const EDITING_HINT = 'Save or cancel your edits to continue';
const UNDO_WHILE_EDITING_HINT = 'Save or cancel your edits first';
const WARNING_ALERT_CLASS = 'border-border-warning-default bg-background-warning-light-hover text-foreground';
const ERROR_ALERT_CLASS = 'border-destructive bg-destructive/10 text-destructive';

// The wizard has already put any failure into `error` (shown by ReviewAlert);
// callers only need to know whether to carry on, e.g. leave edit mode.
function succeeded(action: Promise<void>): Promise<boolean> {
  return action.then(
    () => true,
    () => false,
  );
}

// Line breaks in the message (a list of rejected rows) are kept.
function ReviewAlert({ error, drift }: { error: string | null; drift: boolean }) {
  if (error === null) return null;
  return (
    <div
      role="alert"
      className={cn('rounded-lg border px-3 py-2 text-body-sm-regular whitespace-pre-line', drift ? WARNING_ALERT_CLASS : ERROR_ALERT_CLASS)}
    >
      {error}
    </div>
  );
}

function ReviewNotice({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div role="status" className="rounded-lg border border-border bg-muted px-3 py-2 text-body-sm-regular text-foreground">
      {message}
    </div>
  );
}

interface UndoToastProps {
  lastChange: LastRowChange | null;
  busy: boolean;
  editing: boolean;
  onUndo: () => Promise<void>;
  onDismissChange: () => void;
}

interface UndoPillProps extends UndoToastProps {
  lastChange: LastRowChange;
}

function UndoPill({ lastChange, busy, editing, onUndo, onDismissChange }: UndoPillProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // The timer restarts per change only; a new dismiss callback identity must
  // not keep pushing the timeout back.
  const dismissRef = useRef(onDismissChange);
  useEffect(() => {
    dismissRef.current = onDismissChange;
  });
  // The countdown holds while the admin is reading or reaching for Undo, and
  // while the grid is being edited (Undo is disabled then, so it can't be used).
  const paused = hovered || focused || editing;
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => dismissRef.current(), UNDO_TOAST_MS);
    return () => clearTimeout(timer);
  }, [lastChange, paused]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      className="flex items-center gap-2 self-start rounded-full bg-foreground-ink px-4 py-2 text-body-sm-regular text-foreground-primary-default"
    >
      <span>{lastChange.message}</span>
      <span aria-hidden="true">·</span>
      {/* An undo mid-draft would leave the draft holding values the next
          Save re-applies, so it waits until the draft is saved or dropped. */}
      <button
        type="button"
        disabled={busy || editing}
        title={editing ? UNDO_WHILE_EDITING_HINT : undefined}
        onClick={() => void succeeded(onUndo())}
        className="text-body-sm-medium underline disabled:opacity-50"
      >
        Undo
      </button>
    </div>
  );
}

// The live region stays mounted (empty, off-screen) so a screen reader
// announces the message when it appears; a region inserted with its content
// is often not announced. The pill remounts per change, resetting hover/focus.
function UndoToast(props: UndoToastProps) {
  const { lastChange } = props;
  return (
    <div role="status" className={lastChange === null ? 'sr-only' : undefined}>
      {lastChange !== null && <UndoPill key={lastChange.at} {...props} lastChange={lastChange} />}
    </div>
  );
}

function DiscardEditsConfirm({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  return (
    <div role="alertdialog" aria-label="Discard your edits" className="flex items-center gap-2">
      <p className="text-body-sm-regular">{DISCARD_EDITS_TEXT}</p>
      <Button ref={keepRef} type="button" variant="outline" size="sm" onClick={onKeep}>
        Keep editing
      </Button>
      <Button type="button" variant="destructive" size="sm" onClick={onDiscard}>
        Discard edits
      </Button>
    </div>
  );
}

interface EditControlsProps {
  editing: boolean;
  confirmingCancel: boolean;
  busy: boolean;
  /** The issues banner is showing its own Edit button: one entry point only. */
  startOnBanner: boolean;
  onStart: () => void;
  onCancel: () => void;
  onKeepEditing: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

function EditControls({ editing, confirmingCancel, busy, startOnBanner, onStart, onCancel, onKeepEditing, onDiscard, onSave }: EditControlsProps) {
  if (editing && confirmingCancel) return <DiscardEditsConfirm onKeep={onKeepEditing} onDiscard={onDiscard} />;
  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={busy} onClick={onSave}>
          Save changes
        </Button>
      </div>
    );
  }
  if (startOnBanner) return null;
  return (
    <Button type="button" variant="outline" size="sm" onClick={onStart}>
      <PencilSimple aria-hidden="true" />
      Edit table
    </Button>
  );
}

interface ReviewFooterProps {
  rows: ImportBatchRow[];
  editing: boolean;
  busy: boolean;
  hasSavedChanges: boolean;
  importing: ImportingProgress | null;
  onBack: () => void;
  onDiscard?: () => void;
  onImport: (skipBlocking: boolean) => void;
}

// A commit that lost a race to another one ("already running") starts with
// no total; "Importing 0 of 0…" would read as nothing to do.
const IMPORTING_LABEL_NO_TOTAL = 'Importing…';

function hasKnownTotal({ total }: ImportingProgress): boolean {
  return total > 0;
}

function importingLabel(progress: ImportingProgress): string {
  if (!hasKnownTotal(progress)) return IMPORTING_LABEL_NO_TOTAL;
  return `Importing ${progress.done} of ${progress.total}…`;
}

interface ImportButtonProps {
  importing: ImportingProgress | null;
  disabled: boolean;
  importable: number;
  onImport: () => void;
}

function ImportButton({ importing, disabled, importable, onImport }: ImportButtonProps) {
  if (importing !== null) {
    return (
      <Button type="button" loading>
        {importingLabel(importing)}
      </Button>
    );
  }
  return (
    <Button type="button" disabled={disabled} onClick={onImport}>
      Import {countLabel(importable, 'creator', 'creators')}
    </Button>
  );
}

interface FooterConfirmProps {
  label: string;
  text: string;
  stayLabel: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onStay: () => void;
}

function FooterConfirm({ label, text, stayLabel, confirmLabel, busy, onConfirm, onStay }: FooterConfirmProps) {
  const stayRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    stayRef.current?.focus();
  }, []);
  return (
    <div
      role="alertdialog"
      aria-label={label}
      data-handles-escape=""
      onKeyDown={(event) => {
        if (event.key === 'Escape') onStay();
      }}
      className="flex w-full items-center gap-3"
    >
      <p className="flex-1 text-body-sm-regular">{text}</p>
      <Button ref={stayRef} type="button" variant="outline" onClick={onStay}>
        {stayLabel}
      </Button>
      <Button type="button" variant="destructive" disabled={busy} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </div>
  );
}

type FooterConfirmKind = 'back' | 'discard';

/** What the footer reads while nothing is being edited: the counts, or why
 *  there is nothing to import when every row is already settled elsewhere. */
function footerMeta(importable: number, blocking: number): string {
  if (importable === 0 && blocking === 0) return NOTHING_TO_IMPORT_TEXT;
  return `${importable} to import · ${countLabel(blocking, 'needs fixing', 'need fixing')}`;
}

function ReviewFooter({ rows, editing, busy, hasSavedChanges, importing, onBack, onDiscard, onImport }: ReviewFooterProps) {
  const [confirming, setConfirming] = useState<FooterConfirmKind | null>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const returnFocusToBack = useRef(false);
  const blocking = useMemo(() => countBlocking(rows), [rows]);
  const importable = useMemo(() => countImportable(rows), [rows]);
  const gate = { busy, editing, blocking, importable };
  const meta = editing ? EDITING_HINT : footerMeta(importable, blocking);

  // The Back button is unmounted while the confirmation shows, so focus can
  // only return to it once it is back in the tree.
  useEffect(() => {
    if (confirming !== null || !returnFocusToBack.current) return;
    returnFocusToBack.current = false;
    backRef.current?.focus();
  }, [confirming]);

  function requestBack() {
    if (hasSavedChanges) setConfirming('back');
    else onBack();
  }

  function stay() {
    returnFocusToBack.current = true;
    setConfirming(null);
  }

  if (confirming === 'back') {
    return (
      <FooterConfirm
        label="Go back to column matching"
        text={BACK_CONFIRM_TEXT}
        stayLabel="Stay"
        confirmLabel="Discard and go back"
        busy={busy}
        onStay={stay}
        onConfirm={onBack}
      />
    );
  }
  if (confirming === 'discard' && onDiscard) {
    return (
      <FooterConfirm
        label="Discard this import"
        text={DISCARD_IMPORT_CONFIRM_TEXT}
        stayLabel="Keep import"
        confirmLabel="Discard import"
        busy={busy}
        onStay={stay}
        onConfirm={onDiscard}
      />
    );
  }
  return (
    <div className="flex w-full items-center gap-3">
      <Button ref={backRef} type="button" variant="outline" disabled={busy || editing} onClick={requestBack}>
        Back
      </Button>
      <span className="flex-1 text-body-sm-regular text-muted-foreground">{meta}</span>
      {onDiscard && importing === null && (
        <Button type="button" variant="ghost" disabled={busy || editing} onClick={() => setConfirming('discard')}>
          Discard import
        </Button>
      )}
      {blocking > 0 && (
        <Button type="button" variant="ghost" disabled={isSkipDisabled(gate)} onClick={() => onImport(true)}>
          Skip {countLabel(blocking, 'row', 'rows')} and import
        </Button>
      )}
      <ImportButton importing={importing} disabled={isImportDisabled(gate)} importable={importable} onImport={() => onImport(false)} />
    </div>
  );
}

function isImporting(importing: ImportingProgress | null): importing is ImportingProgress {
  return importing !== null;
}

function showsVersionColumn(target: ImportTarget, rows: ImportBatchRow[]): boolean {
  return target === 'pipeline' && hasRosterMatches(rows);
}

/** An outreach import leaves out creators already on the campaign; say which, so none just disappears from the review. */
function AlreadyOnCampaignNote({ rows, target }: { rows: ImportBatchRow[]; target: ImportTarget }) {
  if (target !== 'outreach') return null;
  const handles = alreadyOnCampaignHandles(rows);
  if (handles.length === 0) return null;
  return (
    <p role="status" className="text-body-sm-regular text-muted-foreground">
      {`${handles.length} already on this campaign, will be skipped (${handles.map((h) => `@${h}`).join(', ')}).`}
    </p>
  );
}

export function ImportReviewStep(props: ImportReviewStepProps) {
  const { rows, onSaveRows, onEditingChange } = props;
  const target = props.target ?? 'pipeline';
  const importing = props.importing ?? null;
  const locked = isImporting(importing);
  const busy = props.busy || locked;
  const contents = useMemo(() => buildReviewTabs(rows, target), [rows, target]);
  const fields = useMemo(() => gridFields(props.expectedFields, props.mapping), [props.expectedFields, props.mapping]);
  const issues = useMemo(() => reviewIssuesMessage(contents, props.campaignPlatform), [contents, props.campaignPlatform]);
  const [tab, setTab] = useState<ReviewTab>(() => initialReviewTab(contents));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<GridDraft | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [focusCell, setFocusCell] = useState<EditTarget | null>(null);
  const [rowOrderSnapshot, setRowOrderSnapshot] = useState<RowOrderSnapshot | null>(null);

  // While editing, rows keep the order they had when editing started, so a
  // save elsewhere (e.g. a version pick) never moves the row being typed in.
  const gridRows = useMemo(() => {
    if (rowOrderSnapshot !== null) return rowsInOrder(rowOrderSnapshot[tab], rows);
    return tabRows(contents, tab);
  }, [rowOrderSnapshot, tab, rows, contents]);

  function saveRows(changes: RowChangeSet, message: string): Promise<boolean> {
    return succeeded(onSaveRows(changes, message));
  }

  function startEditing(cell?: EditTarget) {
    if (editing || locked) return;
    setFocusCell(cell ?? null);
    setDraft(startDraft(contents.all, fields.map((field) => field.key)));
    setRowOrderSnapshot(tabRowOrder(contents));
    setEditing(true);
    onEditingChange?.(true);
  }

  function stopEditing() {
    setEditing(false);
    setConfirmingCancel(false);
    setFocusCell(null);
    setDraft(null);
    setRowOrderSnapshot(null);
    onEditingChange?.(false);
  }

  // Esc and Cancel can fire by accident (Esc also dismisses an error
  // tooltip), so edits that Save would send are only dropped once confirmed.
  function requestCancel() {
    if (confirmingCancel) {
      setConfirmingCancel(false);
      return;
    }
    if (draft !== null && draftHasChanges(draft, rows)) setConfirmingCancel(true);
    else stopEditing();
  }

  async function saveEdit() {
    if (draft === null || busy) return;
    const changes = draftToChangeSet(draft, rows);
    if (isEmptyChangeSet(changes)) {
      stopEditing();
      return;
    }
    // A failed save keeps the draft so nothing typed is lost.
    if (await saveRows(changes, draftChangeMessage(changes))) stopEditing();
  }

  const onDiscard = props.onDiscard;
  const showDuplicatesTab = tab === 'duplicates' && !editing;
  const tabs = TAB_ORDER.map((key) => ({ key, label: tabLabel(contents, key), count: tabCount(contents, key) }));
  // Edit mode has its own Save / Cancel; the banner would only repeat stale counts.
  const bannerIssues = editing ? null : issues;

  // A disabled fieldset disables every control inside it at once — tabs,
  // version toggles, bulk actions — while the import runs.
  return (
    <RowNumberOffsetContext.Provider value={props.rowNumberOffset ?? 0}>
    <fieldset disabled={locked} className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
      <ReviewAlert error={props.error} drift={props.driftRowNumbers.length > 0} />
      <ReviewNotice message={props.notice} />
      <AlreadyOnCampaignNote rows={rows} target={target} />
      <ReviewNotice message={props.engagementReadAsFraction ? ENGAGEMENT_FRACTION_NOTICE : null} />
      <ReviewIssuesBanner issues={bannerIssues} onEdit={() => startEditing()} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PhaseSubTabs bare active={tab} tabs={tabs} onChange={setTab} />
        <EditControls
          editing={editing}
          confirmingCancel={confirmingCancel}
          busy={busy}
          startOnBanner={bannerIssues !== null}
          onStart={() => startEditing()}
          onCancel={requestCancel}
          onKeepEditing={() => setConfirmingCancel(false)}
          onDiscard={stopEditing}
          onSave={() => void saveEdit()}
        />
      </div>
      <ExistingCreatorsBar rows={rows} busy={busy} onSaveRows={saveRows} />
      {showDuplicatesTab ? (
        <DuplicatesTab
          groups={contents.duplicateGroups}
          exactCopies={contents.exactCopies}
          fields={fields}
          campaignPlatform={props.campaignPlatform}
          busy={busy}
          onSaveRows={saveRows}
        />
      ) : (
        <ImportReviewGrid
          target={target}
          rows={gridRows}
          fields={fields}
          campaignPlatform={props.campaignPlatform}
          payoutModel={props.payoutModel}
          showVersion={showsVersionColumn(target, contents.all)}
          fadeKey={tab}
          emptyText={EMPTY_TAB_TEXT[tab]}
          busy={busy}
          editing={editing}
          draft={draft}
          onStartEdit={startEditing}
          focusCell={focusCell}
          onFocusHandled={() => setFocusCell(null)}
          onDraftChange={setDraft}
          onSaveEdit={() => void saveEdit()}
          onCancelEdit={requestCancel}
          onSaveRows={saveRows}
        />
      )}
      <UndoToast lastChange={props.lastChange} busy={busy} editing={editing} onUndo={props.onUndo} onDismissChange={props.onDismissChange} />
      <WizardFooter>
        <ReviewFooter
          rows={rows}
          editing={editing}
          busy={busy}
          hasSavedChanges={props.hasSavedChanges}
          importing={importing}
          onBack={() => void succeeded(props.onBack())}
          onDiscard={onDiscard && (() => void succeeded(onDiscard()))}
          onImport={(skipBlocking) => void succeeded(props.onImport({ skipBlocking }))}
        />
      </WizardFooter>
    </fieldset>
    </RowNumberOffsetContext.Provider>
  );
}
